// src/modules/units/tyres/tyre-management.js
//
// Controller for the standalone Tyre Management area. It owns a full-screen root
// mounted alongside the fleet view, a persistent header with a context-aware back
// button, and a small view-stack router. Opening the area hides the fleet view;
// showing it again is non-destructive (the fleet DOM and its refresh keep running
// underneath). The same create/detail views are also used when Tyre Management is
// opened directly from a wheel on the unit diagram; there is no separate legacy
// wheel-context tyre form any more.

import { renderTyreDashboard } from './views/dashboard/tyredashboard.js';
import { renderTyreRegister } from './views/register/tyreregister.js';
import { renderTyreDetail } from './views/detail/tyredetail.js';
import { renderTyreActionForm } from './views/action/tyreactionform.js';
import { renderTyreAssociationForm } from './views/action/tyreassociationform.js';
import { renderTyreReports } from './views/reports/tyrereports.js';
import { renderTyreForm } from './views/create/newtyreform.js';
import { mountYardScope } from './views/yardscope.js';
import { renderYards } from './views/yards/yards.js';
import { renderSuppliers } from './views/suppliers/suppliers.js';
import { renderTyreCatalogue } from './views/catalogue/tyrecatalogue.js';
import { renderTyreImport } from './views/import/tyreimport.js';
import { renderArchivedTyres } from './views/archive/archivedtyres.js';
import { renderRetreadBatchList } from './views/retread-batch/retreadbatchlist.js';
import { renderRetreadBatchDetail } from './views/retread-batch/retreadbatchdetail.js';
import { renderRetreadBatchForm } from './views/retread-batch/retreadbatchform.js';
import { renderRetreadBatchPicker } from './views/retread-batch/retreadbatchpicker.js';
import { renderRetreadBatchDispatch } from './views/retread-batch/retreadbatchdispatch.js';
import { renderRetreadBatchCancel } from './views/retread-batch/retreadbatchcancel.js';
import { renderRetreadBatchReceive } from './views/retread-batch/retreadbatchreceive.js';
import { getTyreAction } from './shared/tyre-actions.js';
import { canArchiveTyres } from './shared/tyre-access.js';
import { setSelectedYard } from './shared/tyre-scope.js';
import { icon } from './shared/tyre-icons.js';
import { TYRE_STATUS, TYRE_BIN, hasSensor, statusLabel, binLabel } from './shared/tyre-status.js';
import { actionVerb } from './shared/tyre-action-schema.js';
import { esc } from './shared/tyre-format.js';

const ROOT_ID = 'tyremanagementcontainer';
const FLEET_ID = 'unitscontainer';
const HIERARCHY_ID = 'customerhierarchycontainer';

let rootEl = null;
let viewEl = null;
let backLabelEl = null;
let subtitleEl = null;
let yardScope = null; // header yard selector: { refresh, teardown }

// ---------------------------------------------------------------------------
// View-stack router
//
// A minimal navigation stack. Each entry knows how to mount one view and returns
// a teardown. Back pops one level (Register → Dashboard, Detail → Register); at
// the root, Back leaves the tyre area entirely. This gives real navigation depth
// and a correct Back button without imposing routing on the rest of the app, and
// it is the seam a future URL/hash router would plug straight into.
//
// Entry shape: { title, backLabel, mount(viewEl) -> teardown, teardown }
// ---------------------------------------------------------------------------
const stack = [];
const top = () => stack[stack.length - 1] || null;

function disposeTop() {
  const e = top();
  if (e && typeof e.teardown === 'function') { e.teardown(); e.teardown = null; }
}

function renderTop() {
  const e = top();
  if (!e || !viewEl) return;
  if (subtitleEl) subtitleEl.textContent = e.title || '';
  if (backLabelEl) backLabelEl.textContent = stack.length > 1 ? (e.backLabel || 'Back') : 'Back to Fleet';
  // An entry can ask for the wide frame (the multi-pane tyre detail does). The class
  // is the only thing that widens the shared header, so gating it here keeps every
  // other screen — register, dashboard, batch detail — at its normal width.
  if (rootEl) rootEl.classList.toggle('tm-root-wide', !!e.wide);
  e.teardown = e.mount(viewEl) || null;
}

function push(entry) { disposeTop(); stack.push(entry); renderTop(); }
function reset(entry) { while (stack.length) { disposeTop(); stack.pop(); } stack.push(entry); renderTop(); }
// Swap the current top for another entry (e.g. New tyre form → the created
// tyre's detail) without briefly re-mounting the entry beneath.
function replaceTop(entry) { disposeTop(); stack.pop(); stack.push(entry); renderTop(); }

function back() {
  disposeTop();
  stack.pop();
  if (!stack.length) { closeTyreManagement(); return; }
  renderTop();
}

// ---------------------------------------------------------------------------
// Entries
// ---------------------------------------------------------------------------
function dashboardEntry() {
  return {
    title: 'Manage tyres across your fleet',
    backLabel: 'Back to Fleet',
    teardown: null,
    mount: (v) => renderTyreDashboard(v, {
      onAction,
      // A bin tile is a filtered register, which is exactly what Stock / Depot
      // already was — so it routes the same way rather than growing its own screen.
      // `bin` rather than `status`: a bin is derived, so the register has to filter
      // on the derivation too or the list would disagree with the count that
      // opened it.
      onBin: (bin) => push(registerEntry({
        title: binLabel(bin),
        presets: {
          bin,
          // In-Stock tyres are not fitted; the registry drops the fitment columns.
          hasFitment: bin === TYRE_BIN.IN_STOCK ? false : undefined
        }
      }))
    })
  };
}

function registerEntry({ title = 'Tyre Register', presets = {} } = {}) {
  // `state` is owned here so it survives a round-trip into a tyre's detail: the
  // register hydrates from it on mount and writes filters/page back into it.
  const state = {};
  // Picker modes: `assoc` sends a selected tyre into the fit/move flow; `action`
  // sends it into a capture form; otherwise a tyre opens its detail.
  const assoc = presets.assoc;
  const action = presets.action;
  return {
    title,
    backLabel: 'Back',
    teardown: null,
    mount: (v) => renderTyreRegister(v, {
      state,
      presets,
      onOpen: (tyre) => {
        if (assoc) push(associationFormEntry(tyre, hasSensor(tyre) ? 'move' : 'fit'));
        else if (action) push(actionFormEntry(tyre, action));
        else push(detailEntry(tyre));
      }
    })
  };
}

function detailEntry(tyre) {
  return {
    title: tyre && tyre.externalTyreId ? `Tyre ${tyre.externalTyreId}` : 'Tyre detail',
    backLabel: 'Back',
    // The multi-pane detail uses the wide frame (header + content) on large screens.
    wide: true,
    teardown: null,
    mount: (v) => renderTyreDetail(v, {
      tyre,
      // `current` is the detail's freshest record (post-fetch); prefer it.
      onAction: (key, current) => {
        const t = current || tyre;
        if (key === 'edit') push(editTyreEntry(t));
        else if (key === 'fit' || key === 'move' || key === 'remove') push(associationFormEntry(t, key));
        else push(actionFormEntry(t, key));
      }
    })
  };
}

// After archiving, do not remount the detail view — the detail endpoint now 404s
// for this tyre. Preserve the existing register entry and its filters (it
// refetches off CHANGED, so the tyre is already gone from it); a direct
// wheel-context detail returns to Fleet.
function leaveArchivedTyre() {
  disposeTop();
  stack.pop(); // edit form
  stack.pop(); // archived tyre's detail (already unmounted when the form was pushed)
  if (stack.length) renderTop();
  else closeTyreManagement();
}

function editTyreEntry(tyre) {
  return {
    title: `Edit${tyre?.externalTyreId ? ` · ${tyre.externalTyreId}` : ''}`,
    backLabel: 'Cancel',
    teardown: null,
    mount: (v) => renderTyreForm(v, {
      mode: 'edit',
      tyre,
      onClose: () => back(),
      onArchived: leaveArchivedTyre
    })
  };
}

function actionFormEntry(tyre, actionType) {
  const label = `${actionVerb(actionType)}${tyre?.externalTyreId ? ` · ${tyre.externalTyreId}` : ''}`;
  return {
    title: label,
    backLabel: 'Cancel',
    teardown: null,
    mount: (v) => renderTyreActionForm(v, {
      tyre,
      actionType,
      onClose: () => back()
    })
  };
}

function associationFormEntry(tyre, mode) {
  const verb = mode === 'remove' ? 'Remove' : mode === 'move' ? 'Move' : 'Fit';
  return {
    title: `${verb}${tyre?.externalTyreId ? ` · ${tyre.externalTyreId}` : ''}`,
    backLabel: 'Cancel',
    teardown: null,
    mount: (v) => renderTyreAssociationForm(v, {
      tyre,
      mode,
      onClose: () => back()
    })
  };
}

function reportsEntry() {
  return {
    title: 'Reports',
    backLabel: 'Back',
    teardown: null,
    mount: (v) => renderTyreReports(v, {})
  };
}

// Yards: create and switch between the customer's yards. Selecting one sets the
// shared scope (tyre-scope.js) that every other screen already reads, so this is
// the management surface for the same value the header selector switches.
function yardsEntry() {
  return {
    title: 'Yards',
    backLabel: 'Back',
    teardown: null,
    mount: (v) => renderYards(v, {})
  };
}

function suppliersEntry() {
  return {
    title: 'Suppliers',
    backLabel: 'Back',
    teardown: null,
    mount: (v) => renderSuppliers(v, {})
  };
}

// Retread batches: browse consignments sent to a supplier, and open one to see its
// tyres and status. A row opens the batch detail, the retread-side parallel to the
// register -> tyre-detail flow. (Read-only for now; dispatch/receive/cancel land
// with the write forms.)
function retreadBatchListEntry() {
  return {
    title: 'Retread batches',
    backLabel: 'Back',
    teardown: null,
    mount: (v) => renderRetreadBatchList(v, {
      onOpen: (batch) => push(retreadBatchDetailEntry(batch)),
      onNew: () => push(retreadBatchFormEntry({ mode: 'create' }))
    })
  };
}

function retreadBatchDetailEntry(batch) {
  return {
    title: batch && batch.reference ? `Batch ${batch.reference}` : 'Retread batch',
    backLabel: 'Back',
    teardown: null,
    mount: (v) => renderRetreadBatchDetail(v, {
      batch,
      onAddTyres: (fresh) => push(retreadBatchPickerEntry(fresh)),
      onEdit: (fresh) => push(retreadBatchFormEntry({ mode: 'edit', batch: fresh })),
      onDispatch: (fresh, supplierName) => push(retreadBatchDispatchEntry(fresh, supplierName)),
      onReceive: (fresh) => push(retreadBatchReceiveEntry(fresh)),
      onCancel: (fresh) => push(retreadBatchCancelEntry(fresh))
    })
  };
}

// Receive tyres back against a batch (from the detail's Receive button). On a
// successful receive, pop back to the detail (refetched off the event).
function retreadBatchReceiveEntry(batch) {
  return {
    title: 'Receive tyres',
    backLabel: 'Back',
    teardown: null,
    // The per-tyre receive rows want room (id · Retreaded｜Rejected｜Not received ·
    // cost/reason), so this screen uses the wide frame on large screens like the
    // multi-pane tyre detail does.
    wide: true,
    mount: (v) => renderRetreadBatchReceive(v, {
      batch,
      onClose: () => back(),
      onDone: () => back()
    })
  };
}

// Confirm and send a DRAFT for retread (from the detail's Dispatch button). On a
// successful dispatch, pop back to the detail (refetched off the event).
function retreadBatchDispatchEntry(batch, supplierName) {
  return {
    title: 'Dispatch batch',
    backLabel: 'Back',
    teardown: null,
    mount: (v) => renderRetreadBatchDispatch(v, {
      batch,
      supplierName,
      onClose: () => back(),
      onDone: () => back()
    })
  };
}

// Confirm and cancel a batch (from the detail's Cancel button).
function retreadBatchCancelEntry(batch) {
  return {
    title: 'Cancel batch',
    backLabel: 'Back',
    teardown: null,
    mount: (v) => renderRetreadBatchCancel(v, {
      batch,
      onClose: () => back(),
      onDone: () => back()
    })
  };
}

// Pick in-stock tyres to add to a DRAFT (from the detail's Add tyres button). On
// close, pop back to the detail, which has refetched off RETREAD_BATCH_CHANGED.
function retreadBatchPickerEntry(batch) {
  return {
    title: 'Add tyres',
    backLabel: 'Back',
    teardown: null,
    mount: (v) => renderRetreadBatchPicker(v, {
      batch,
      onClose: () => back()
    })
  };
}

// Create a draft (from the list's New button) or edit a draft's details. A
// successful create lands on the new batch's detail; a successful edit pops back
// to the detail beneath, which refetches off RETREAD_BATCH_CHANGED.
function retreadBatchFormEntry({ mode = 'create', batch = null } = {}) {
  const title = mode === 'edit'
    ? (batch && batch.reference ? `Edit ${batch.reference}` : 'Edit batch')
    : 'New retread batch';
  return {
    title,
    backLabel: 'Cancel',
    teardown: null,
    mount: (v) => renderRetreadBatchForm(v, {
      mode,
      batch,
      onClose: () => back(),
      onSaved: (saved) => {
        if (mode === 'edit') back();
        else replaceTop(retreadBatchDetailEntry(saved));
      }
    })
  };
}

// Archived tyres (administrator only): the soft-deleted tyres, with Restore.
// "Open tyre" after a restore pushes the restored tyre's detail on top, so Back
// returns here — the admin is usually working through a list of restores.
function archivedTyresEntry() {
  return {
    title: 'Archived tyres',
    backLabel: 'Back',
    teardown: null,
    mount: (v) => renderArchivedTyres(v, {
      onOpenTyre: (tyre) => push(detailEntry(tyre))
    })
  };
}

function catalogueEntry() {
  return {
    title: 'Tyre catalogue',
    backLabel: 'Back',
    teardown: null,
    mount: (v) => renderTyreCatalogue(v, {})
  };
}

// Import tyres from a spreadsheet (Setup → Import tyres). "View tyres" swaps this
// screen for the register scoped to the yard just imported into — the user's
// explicit ask, so it moves the shared yard scope the same way the Yards view does.
function importEntry() {
  return {
    title: 'Import tyres',
    backLabel: 'Back',
    // The review/results tables carry a details column that wants the room.
    wide: true,
    teardown: null,
    mount: (v) => renderTyreImport(v, {
      onClose: () => back(),
      onOpenRegister: (yard) => {
        if (yard) setSelectedYard(yard);
        replaceTop(registerEntry({ title: 'Tyre Register' }));
      }
    })
  };
}

function newTyreEntry(createCtx = {}) {
  return {
    title: createCtx.targetSensorId ? 'New tyre · fit to wheel' : 'New tyre',
    backLabel: 'Cancel',
    teardown: null,
    mount: (v) => renderTyreForm(v, {
      mode: 'create',
      targetSensorId: createCtx.targetSensorId,
      fitTargetLabel: createCtx.fitTargetLabel,
      onAssociated: createCtx.onAssociated,
      onClose: () => back(),
      // Land on the freshly-created tyre's detail. From a wheel-context create
      // the form has already fitted the tyre to that wheel's sensor by this point.
      onCreated: (tyre) => replaceTop(detailEntry(tyre)),
      // The entered ID belonged to an archived tyre and the admin restored it:
      // land on that tyre's detail instead of a create that could never succeed.
      onRestored: (tyre) => replaceTop(detailEntry(tyre))
    })
  };
}

function actionEntry(actionId) {
  const action = getTyreAction(actionId);
  return {
    title: action?.label || 'Tyre action',
    backLabel: 'Back',
    teardown: null,
    mount: (v) => {
      const t = document.createElement('template');
      t.innerHTML = `
        <div class="tm-placeholder">
          <span class="tm-placeholder-badge tm-tone-${action?.tone || 'slate'}">${icon(action?.icon || 'tyre')}</span>
          <h2 class="tm-placeholder-title">${esc(action?.label || 'Tyre action')}</h2>
          <p class="tm-placeholder-blurb">${esc(action?.blurb || '')}</p>
          <p class="tm-placeholder-note">This screen is next on the build plan.</p>
        </div>`;
      v.replaceChildren(t.content.firstElementChild);
      return () => { };
    }
  };
}

// Ring/action routing. Register and Stock/Depot are the same list with different
// presets — one source of truth, two entry points. The inspect/repair/retread
// tiles open that same list in action-picker mode (pick a tyre → its capture
// form), so each lifecycle action has exactly one write path.
function onAction(actionId) {
  switch (actionId) {
    case 'new':
      push(newTyreEntry());
      break;
    case 'register':
      push(registerEntry({ title: 'Tyre Register' }));
      break;
    case 'stock':
      push(registerEntry({
        title: 'Stock / Depot',
        // In stock means not fitted, so the registry drops the three fitment columns.
        presets: { status: TYRE_STATUS.IN_STOCK, hasFitment: false }
      }));
      break;
    case 'inspect':
      push(registerEntry({ title: 'Inspect a tyre', presets: { action: 'inspect', banner: 'Select a tyre to log an inspection.' } }));
      break;
    case 'repair':
      push(registerEntry({ title: 'Repair a tyre', presets: { action: 'repair', banner: 'Select a tyre to log a repair.' } }));
      break;
    case 'retread':
      // The retread flow is batch-based now: the Retread tile opens the batch list
      // (the entry point for creating, dispatching and receiving consignments)
      // rather than the old one-tyre-at-a-time action picker. The legacy
      // single-tyre retread action still exists on a tyre's own detail.
      push(retreadBatchListEntry());
      break;
    case 'fit':
      push(registerEntry({ title: 'Fit a tyre to a sensor', presets: { assoc: true, banner: 'Select a tyre to fit to a sensor (already-fitted tyres can be moved).' } }));
      break;
    case 'yards':
      push(yardsEntry());
      break;
    case 'suppliers':
      push(suppliersEntry());
      break;
    case 'catalogue':
      push(catalogueEntry());
      break;
    case 'import':
      push(importEntry());
      break;
    case 'retire':
      push(registerEntry({ title: 'Retire a tyre', presets: { action: 'retire', banner: 'Select a tyre to take out of the fleet.' } }));
      break;
    case 'reports':
      push(reportsEntry());
      break;
    case 'archived':
      // The tile is hidden from non-administrators; guard the route as well so it
      // cannot be reached any other way. The server returns 403 regardless.
      if (canArchiveTyres()) push(archivedTyresEntry());
      break;
    default:
      push(actionEntry(actionId));
  }
}

// ---------------------------------------------------------------------------
// Mount + open/close
// ---------------------------------------------------------------------------
function ensureMounted() {
  if (rootEl) return rootEl;

  const container = document.getElementById('container');
  if (!container) return null;

  const tmpl = document.createElement('template');
  tmpl.innerHTML = `
    <div id="${ROOT_ID}" class="tm-root" hidden>
      <header class="tm-header">
        <button type="button" class="tm-back" data-tm-back>
          <span class="tm-back-icon">${icon('back')}</span>
          <span data-tm-backlabel>Back to Fleet</span>
        </button>
        <div class="tm-header-titles">
          <h1 class="tm-title">Tyre Management</h1>
          <p class="tm-subtitle" data-tm-subtitle>Manage tyres across your fleet</p>
        </div>
        <div class="tm-yardscope-slot" data-tm-yardscope></div>
      </header>
      <main class="tm-view" data-tm-view></main>
    </div>
  `;
  rootEl = tmpl.content.firstElementChild;
  container.appendChild(rootEl);

  viewEl = rootEl.querySelector('[data-tm-view]');
  backLabelEl = rootEl.querySelector('[data-tm-backlabel]');
  subtitleEl = rootEl.querySelector('[data-tm-subtitle]');

  rootEl.querySelector('[data-tm-back]').addEventListener('click', back);

  // Primary yard selector lives in the header, in the slot that used to just
  // balance the back button. Mounted once; the persistent root means its
  // SCOPE_CHANGED subscription lives for the app session, like the back handler.
  const yardSlot = rootEl.querySelector('[data-tm-yardscope]');
  if (yardSlot) yardScope = mountYardScope(yardSlot);

  return rootEl;
}

// Reveal the standalone area over the fleet: hide the fleet/hierarchy (kept in the
// DOM and still refreshing underneath) and show the tyre root. Shared by every
// entry point so "show the area" has a single definition and cannot drift.
function revealArea() {
  const fleet = document.getElementById(FLEET_ID);
  const hierarchy = document.getElementById(HIERARCHY_ID);
  if (fleet) fleet.style.display = 'none';
  if (hierarchy) hierarchy.style.display = 'none';

  rootEl.hidden = false;
  // Keep the header yard list current on each entry (one may have been added
  // since last open). Fire-and-forget: screens paint immediately for the
  // persisted scope, and if that scope has since vanished refresh() corrects it.
  yardScope?.refresh();
}

export function openTyreManagement() {
  if (!ensureMounted()) return;
  revealArea();
  reset(dashboardEntry());
  rootEl.scrollIntoView({ block: 'start' });
}

/**
 * Open the normal New Tyre screen from a specific wheel. Saving creates/updates
 * the tyre record and then immediately associates it with targetSensorId. This is
 * the single replacement for the retired wheel-context tyre modal.
 *
 * @param {{ targetSensorId?: string|number, fitTargetLabel?: string, onAssociated?: Function }} ctx
 * @returns {boolean} true when the form opened.
 */
export function openNewTyreForWheel(ctx = {}) {
  const targetSensorId = ctx.targetSensorId ?? null;
  if (targetSensorId === null || targetSensorId === undefined || targetSensorId === '') return false;
  if (!ensureMounted()) return false;

  revealArea();
  reset(newTyreEntry({
    targetSensorId,
    fitTargetLabel: ctx.fitTargetLabel || `sensor ${targetSensorId}`,
    onAssociated: ctx.onAssociated
  }));
  rootEl.scrollIntoView({ block: 'start' });
  return true;
}

/**
 * Open one tyre's detail directly — the entry point used from the unit diagram's
 * "Edit Tyre Info". Detail is made the *root* of the stack, so Back returns
 * straight to the fleet the user came from rather than to the dashboard they
 * never visited; the header's Back button reads "Back to Fleet" automatically.
 * The detail view refetches the authoritative record by id, so `tyre` only needs
 * a valid externalTyreId — any extra fields it already carries are used purely
 * for a richer first paint and are then overwritten by the fetch.
 *
 * @param {{ externalTyreId?: string }} tyre seed row (must carry externalTyreId)
 * @returns {boolean} true if detail opened; false if there was no tyre to show.
 */
export function openTyreDetail(tyre) {
  if (!tyre || !tyre.externalTyreId) return false;
  if (!ensureMounted()) return false;
  revealArea();
  reset(detailEntry(tyre));
  rootEl.scrollIntoView({ block: 'start' });
  return true;
}

export function closeTyreManagement() {
  disposeTop();
  stack.length = 0;
  if (rootEl) rootEl.hidden = true;

  const fleet = document.getElementById(FLEET_ID);
  if (fleet) fleet.style.display = 'block';
}

/**
 * Wire the toolbar entry point. Safe to call more than once.
 */
export function initTyreManagement() {
  const btn = document.getElementById('tyremanagementbuttonmain');
  if (!btn || btn.dataset.tmBound === 'true') return;
  btn.dataset.tmBound = 'true';
  btn.addEventListener('click', (e) => {
    e.stopImmediatePropagation();
    openTyreManagement();
  });
}
